import { useState, useId, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Loader2, CalendarDays, CheckCircle2 } from "lucide-react";
import { Turnstile } from "./Turnstile";

const TIME_WINDOWS = [
  {
    value: "Morning (8:00 AM - 12:00 PM)",
    label: "Morning",
    hint: "8:00 AM – 12:00 PM",
  },
  {
    value: "Afternoon (12:00 PM - 4:00 PM)",
    label: "Afternoon",
    hint: "12:00 PM – 4:00 PM",
  },
  {
    value: "Evening (4:00 PM - 8:00 PM)",
    label: "Evening",
    hint: "4:00 PM – 8:00 PM",
  },
] as const;

const TIME_WINDOW_VALUES = TIME_WINDOWS.map((w) => w.value) as unknown as [
  string,
  ...string[],
];

const SITE_KEY = import.meta.env["VITE_TURNSTILE_SITE_KEY"] as
  string | undefined;

/** Local (not UTC) YYYY-MM-DD, so "today" means today in the visitor's timezone. */
function toLocalIsoDate(d: Date) {
  const offset = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - offset).toISOString().slice(0, 10);
}

const formSchema = z.object({
  preferredDate: z.string().min(1, "Please choose a date"),
  timeWindow: z.enum(TIME_WINDOW_VALUES, {
    errorMap: () => ({ message: "Please pick a time of day" }),
  }),
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  phone: z
    .string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^[\d\s\-()+.]+$/, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email address"),
  address: z.string().min(5, "Please enter your full street address"),
  city: z.string().min(2, "City is required"),
  state: z
    .string()
    .min(2, "State is required")
    .refine(
      (s) =>
        s.trim().toUpperCase() === "MN" ||
        s.trim().toUpperCase() === "MINNESOTA",
      "Sorry, we only serve Minnesota",
    ),
  zipCode: z
    .string()
    .min(5, "ZIP code is required")
    .max(10, "Invalid ZIP code"),
  // Honeypot — must stay empty. Hidden from humans, irresistible to form-filling bots.
  website: z.string().max(0).optional(),
});

type FormData = z.infer<typeof formSchema>;

export const AppointmentForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [formStartTime] = useState(() => Date.now());
  const formId = useId();

  const today = useMemo(() => toLocalIsoDate(new Date()), []);
  const maxDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return toLocalIsoDate(d);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(formSchema) });

  const selectedWindow = watch("timeWindow");

  const onSubmit = async (data: FormData) => {
    // Bot trap 1: honeypot. Pretend it worked so the bot does not learn anything.
    if (data.website && data.website.length > 0) {
      setSubmitted(true);
      reset();
      return;
    }

    // Bot trap 2: nobody fills out nine fields in under two seconds.
    if (Date.now() - formStartTime < 2000) {
      setSubmitted(true);
      reset();
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${import.meta.env["VITE_SUPABASE_URL"]}/functions/v1/appointment-request`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env["VITE_SUPABASE_PUBLISHABLE_KEY"]}`,
          },
          body: JSON.stringify({
            preferredDate: data.preferredDate,
            timeWindow: data.timeWindow,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            email: data.email,
            address: data.address,
            city: data.city,
            state: data.state,
            zipCode: data.zipCode,
            source: "pick_a_time",
            turnstileToken: captchaToken,
          }),
        },
      );

      const payload = (await res.json().catch(() => ({}))) as {
        success?: boolean;
        error?: string;
      };

      if (!res.ok || !payload.success) {
        // A real person blocked by the challenge needs a way forward, not a dead end.
        toast.error(
          payload.error ||
            "We couldn't submit that. Please try again, or call us at (612) 298-9865.",
        );
        return;
      }

      setSubmitted(true);
      reset();
    } catch (err) {
      console.error("Appointment request failed:", err);
      toast.error(
        "Something went wrong. Please try again or call us at (612) 298-9865.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-card rounded-2xl p-8 shadow-lg border border-border/50 text-center">
        <CheckCircle2
          className="w-12 h-12 text-secondary mx-auto mb-4"
          aria-hidden="true"
        />
        <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
          You're on the schedule request list
        </h2>
        <p className="text-muted-foreground mb-6">
          We've got your details. Once we build the route for that day we'll
          call you with a tighter arrival window. Check your email for a
          confirmation.
        </p>
        <a href="tel:+16122989865">
          <Button variant="outline" className="font-semibold">
            Need us sooner? Call (612) 298-9865
          </Button>
        </a>
      </div>
    );
  }

  const err = (field: keyof FormData) =>
    errors[field] ? (
      <p
        id={`${formId}-${field}-error`}
        className="text-xs text-destructive mt-1"
        role="alert"
      >
        {errors[field]?.message as string}
      </p>
    ) : null;

  const inputProps = (field: keyof FormData) => ({
    className: errors[field] ? "border-destructive" : "",
    "aria-invalid": errors[field] ? ("true" as const) : ("false" as const),
    "aria-describedby": errors[field] ? `${formId}-${field}-error` : undefined,
  });

  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border/50">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        aria-label="Request an inspection appointment"
      >
        {/* Honeypot — off-screen, hidden from assistive tech, not tabbable. */}
        <div className="absolute left-[-9999px]" aria-hidden="true">
          <label htmlFor={`${formId}-website`}>Website (leave blank)</label>
          <Input
            id={`${formId}-website`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("website")}
          />
        </div>

        <fieldset className="space-y-4">
          <legend className="text-lg font-heading font-bold text-foreground mb-1">
            Let's schedule your inspection
          </legend>

          <div>
            <Label
              htmlFor={`${formId}-preferredDate`}
              className="font-semibold"
            >
              Choose the date you'd like us to come out{" "}
              <span className="text-destructive">*</span>
            </Label>
            <p className="text-sm text-muted-foreground mt-0.5 mb-2">
              We'll do our best to accommodate your preference.
            </p>
            <div className="relative">
              <CalendarDays
                className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                aria-hidden="true"
              />
              <Input
                id={`${formId}-preferredDate`}
                type="date"
                min={today}
                max={maxDate}
                className={`pl-9 ${errors.preferredDate ? "border-destructive" : ""}`}
                aria-invalid={errors.preferredDate ? "true" : "false"}
                aria-describedby={
                  errors.preferredDate
                    ? `${formId}-preferredDate-error`
                    : undefined
                }
                {...register("preferredDate")}
              />
            </div>
            {err("preferredDate")}
          </div>

          <div>
            <span
              className="font-semibold text-sm"
              id={`${formId}-timeWindow-label`}
            >
              What time of day works best for you?{" "}
              <span className="text-destructive">*</span>
            </span>
            <p className="text-sm text-muted-foreground mt-0.5 mb-2">
              Once we build our route for the day, we'll call and update you
              with a more accurate arrival window.
            </p>
            <div
              role="radiogroup"
              aria-labelledby={`${formId}-timeWindow-label`}
              className="grid gap-2 sm:grid-cols-3"
            >
              {TIME_WINDOWS.map((w) => {
                const active = selectedWindow === w.value;
                return (
                  <button
                    key={w.value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() =>
                      setValue("timeWindow", w.value, { shouldValidate: true })
                    }
                    className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                      active
                        ? "border-secondary bg-secondary/10 ring-2 ring-secondary"
                        : "border-border hover:border-secondary/60 hover:bg-muted/50"
                    }`}
                  >
                    <span className="block font-semibold text-foreground">
                      {w.label}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {w.hint}
                    </span>
                  </button>
                );
              })}
            </div>
            {/* Registered so RHF owns the value; the visible control is the button group above. */}
            <input type="hidden" {...register("timeWindow")} />
            {err("timeWindow")}
          </div>
        </fieldset>

        <fieldset className="space-y-4 border-t border-border pt-6">
          <legend className="text-lg font-heading font-bold text-foreground mb-1">
            Your contact &amp; address information
          </legend>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`${formId}-firstName`}>
                First name <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-firstName`}
                autoComplete="given-name"
                {...inputProps("firstName")}
                {...register("firstName")}
              />
              {err("firstName")}
            </div>
            <div>
              <Label htmlFor={`${formId}-lastName`}>
                Last name <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-lastName`}
                autoComplete="family-name"
                {...inputProps("lastName")}
                {...register("lastName")}
              />
              {err("lastName")}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`${formId}-phone`}>
                Phone number <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-phone`}
                type="tel"
                autoComplete="tel"
                placeholder="(612) 555-0123"
                {...inputProps("phone")}
                {...register("phone")}
              />
              {err("phone")}
            </div>
            <div>
              <Label htmlFor={`${formId}-email`}>
                Email <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-email`}
                type="email"
                autoComplete="email"
                {...inputProps("email")}
                {...register("email")}
              />
              {err("email")}
            </div>
          </div>

          <div>
            <Label htmlFor={`${formId}-address`}>
              Street address <span className="text-destructive">*</span>
            </Label>
            <Input
              id={`${formId}-address`}
              autoComplete="street-address"
              {...inputProps("address")}
              {...register("address")}
            />
            {err("address")}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="col-span-2">
              <Label htmlFor={`${formId}-city`}>
                City <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-city`}
                autoComplete="address-level2"
                {...inputProps("city")}
                {...register("city")}
              />
              {err("city")}
            </div>
            <div>
              <Label htmlFor={`${formId}-state`}>
                State <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-state`}
                autoComplete="address-level1"
                defaultValue="MN"
                {...inputProps("state")}
                {...register("state")}
              />
              {err("state")}
            </div>
            <div>
              <Label htmlFor={`${formId}-zipCode`}>
                ZIP <span className="text-destructive">*</span>
              </Label>
              <Input
                id={`${formId}-zipCode`}
                autoComplete="postal-code"
                inputMode="numeric"
                {...inputProps("zipCode")}
                {...register("zipCode")}
              />
              {err("zipCode")}
            </div>
          </div>
        </fieldset>

        {SITE_KEY ? (
          <Turnstile siteKey={SITE_KEY} onVerify={setCaptchaToken} />
        ) : null}

        <Button
          type="submit"
          disabled={isSubmitting}
          size="lg"
          className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-6"
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2
                className="w-4 h-4 mr-2 animate-spin"
                aria-hidden="true"
              />
              Submitting...
            </>
          ) : (
            <>
              Request my appointment{" "}
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </>
          )}
        </Button>

        <p className="text-xs text-muted-foreground text-center">
          Prefer to talk? Call{" "}
          <a
            href="tel:+16122989865"
            className="font-semibold text-primary hover:underline"
          >
            (612) 298-9865
          </a>
          . Cedar Creek Construction · Lic. BC645307
        </p>
      </form>
    </div>
  );
};
